import BarChartIcon from '@mui/icons-material/BarChart';
import PieChartIcon from '@mui/icons-material/PieChart';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import StackedBarChartIcon from '@mui/icons-material/StackedBarChart';
import VisibilityIcon from '@mui/icons-material/Visibility';
import InsertChartIcon from '@mui/icons-material/InsertChart';
import RadarIcon from '@mui/icons-material/Radar';
import SsidChartIcon from '@mui/icons-material/SsidChart';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import StarIcon from '@mui/icons-material/Star';
import { Box, FormControl, IconButton, InputLabel, ListItemIcon, ListItemText, Menu, MenuItem, Select, Tooltip, Typography } from '@mui/material';
import { useContext, useState } from 'react';
import { createPortal } from 'react-dom';
import { ConfigurableChartDashboard, DASHBOARD_CHART_VIEW_CONFIG, DashboardChartView } from '../types';
import { DashboardCardHeaderActionsContext } from './DashboardCard';

const viewDetails: Record<DashboardChartView, { label: string; icon: React.ReactNode }> = {
    bar: { label: 'Bar Chart', icon: <BarChartIcon fontSize="small" sx={{ color: '#3F51B5' }} /> },
    horizontalBar: { label: 'Horizontal Bar', icon: <StackedBarChartIcon fontSize="small" sx={{ color: '#00897B', transform: 'rotate(90deg)' }} /> },
    pie: { label: 'Donut Chart', icon: <PieChartIcon fontSize="small" sx={{ color: '#9C27B0' }} /> },
    line: { label: 'Line Chart', icon: <ShowChartIcon fontSize="small" sx={{ color: '#1565C0' }} /> },
    area: { label: 'Area Chart', icon: <InsertChartIcon fontSize="small" sx={{ color: '#5E35B1' }} /> },
    spline: { label: 'Spline Chart', icon: <SsidChartIcon fontSize="small" sx={{ color: '#00ACC1' }} /> },
    splineArea: { label: 'Spline Area Chart', icon: <InsertChartIcon fontSize="small" sx={{ color: '#43A047' }} /> },
    stackedBar: { label: 'Stacked Bar', icon: <StackedBarChartIcon fontSize="small" sx={{ color: '#FB8C00' }} /> },
    comparisonBar: { label: 'Comparison Bar', icon: <CompareArrowsIcon fontSize="small" sx={{ color: '#E53935' }} /> },
    serviceComparison: { label: 'Comparison Chart', icon: <BarChartIcon fontSize="small" sx={{ color: '#1976D2' }} /> },
    fullPie: { label: 'Pie Chart', icon: <PieChartIcon fontSize="small" sx={{ color: '#EC407A' }} /> },
    polar: { label: 'Polar Chart', icon: <RadarIcon fontSize="small" sx={{ color: '#546E7A' }} /> },
};

const getViewLabel = (dashboard: ConfigurableChartDashboard, view: DashboardChartView) =>
    dashboard === 'studentServices' && view === 'comparisonBar' ? 'Tornado Chart' : viewDetails[view].label;

interface Props {
    dashboard: ConfigurableChartDashboard;
    value: DashboardChartView;
    onChange: (view: DashboardChartView) => void;
    iconOnly?: boolean;
}

export const DashboardChartViewSelect = ({ dashboard, value, onChange, iconOnly = false }: Props) => {
    const config = DASHBOARD_CHART_VIEW_CONFIG[dashboard];
    const headerActionsElement = useContext(DashboardCardHeaderActionsContext);
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const usesIconMenu = iconOnly || headerActionsElement !== undefined;
    const options = config.allowedViews.map(view => <MenuItem key={view} value={view} selected={view === value}
        onClick={usesIconMenu ? () => { onChange(view); setAnchorEl(null); } : undefined}>
        <ListItemIcon>{viewDetails[view].icon}</ListItemIcon>
        <ListItemText primary={view === config.recommendedView
            ? <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <Typography variant="caption" color="primary" fontWeight={600} lineHeight={1.1}>Recommended</Typography>
                <Typography variant="body1">{getViewLabel(dashboard, view)}</Typography>
            </Box>
            : getViewLabel(dashboard, view)} />
    </MenuItem>);

    const iconSelector = <>
        <Tooltip title={`View as: ${getViewLabel(dashboard, value)}`}>
            <IconButton size="small" sx={{ color: 'white' }} aria-label="View as" onClick={event => setAnchorEl(event.currentTarget)}>
                <VisibilityIcon fontSize="small" />
            </IconButton>
        </Tooltip>
        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}
            PaperProps={{ sx: { minWidth: 250, p: 1 } }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', px: 1, pb: .5 }}>Visualization</Typography>
            <MenuItem sx={{ border: '1px solid', borderColor: 'primary.main', borderRadius: 1, mb: .75 }}>
                <ListItemIcon>{viewDetails[value].icon}</ListItemIcon>
                <ListItemText primary={getViewLabel(dashboard, value)} />
                <StarIcon color="primary" fontSize="small" />
            </MenuItem>
            <Box sx={{ pt: .25 }}>{options}</Box>
        </Menu>
    </>;

    if (iconOnly) return iconSelector;
    if (headerActionsElement !== undefined) {
        return headerActionsElement ? createPortal(iconSelector, headerActionsElement) : null;
    }

    return <FormControl size="small" sx={{ minWidth: 190 }}>
        <InputLabel id={`${dashboard}-chart-view-label`}>View as</InputLabel>
        <Select
            labelId={`${dashboard}-chart-view-label`}
            label="View as"
            value={value}
            onChange={event => onChange(event.target.value as DashboardChartView)}
            renderValue={selected => <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {viewDetails[selected].icon}{getViewLabel(dashboard, selected)}
            </Box>}
        >
            {options}
        </Select>
    </FormControl>;
};
